/**
 * Camera List Component
 *
 * Displays camera configurations in a grid layout with status indicators.
 * Each card shows camera details, capture schedule, and action buttons.
 */

import { useState } from 'react';
import {
  Camera,
  MapPin,
  Building2,
  FileText,
  Clock,
  MoreVertical,
  Pencil,
  Trash2,
  Wifi,
  WifiOff,
  Play,
  Pause,
  Loader2,
  CheckCircle2,
  XCircle,
  Zap,
  Copy,
  Check,
} from 'lucide-react';
import type { CameraConfiguration } from '../../hooks/useCameras';
import { ActivityTriggerConfigPanel } from './ActivityTriggerConfigPanel';

interface CameraListProps {
  cameras: CameraConfiguration[];
  isAdmin: boolean;
  onEdit: (camera: CameraConfiguration) => void;
  onDelete: (id: string) => void;
  onTest: (id: string) => void;
  isDeleting: boolean;
  testingId: string | null;
  testResult: { id: string; isOnline: boolean; error?: string } | null;
}

export function CameraList({
  cameras,
  isAdmin,
  onEdit,
  onDelete,
  onTest,
  isDeleting,
  testingId,
  testResult,
}: CameraListProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cameras.map((camera) => (
        <CameraCard
          key={camera.id}
          camera={camera}
          isAdmin={isAdmin}
          onEdit={() => onEdit(camera)}
          onDelete={() => onDelete(camera.id)}
          onTest={() => onTest(camera.id)}
          isDeleting={isDeleting}
          isTesting={testingId === camera.id}
          testResult={testResult?.id === camera.id ? testResult : null}
        />
      ))}
    </div>
  );
}

interface CameraCardProps {
  camera: CameraConfiguration;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onTest: () => void;
  isDeleting: boolean;
  isTesting: boolean;
  testResult: { isOnline: boolean; error?: string } | null;
}

function CameraCard({
  camera,
  isAdmin,
  onEdit,
  onDelete,
  onTest,
  isDeleting,
  isTesting,
  testResult,
}: CameraCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(camera.cameraId);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch {
      // Fallback for older browsers
    }
  };

  // Determine status based on last capture status and test result
  const getStatusDisplay = () => {
    if (testResult) {
      return testResult.isOnline
        ? { color: 'bg-emerald-500', label: 'Online', icon: Wifi }
        : { color: 'bg-red-500', label: 'Offline', icon: WifiOff };
    }
    if (camera.lastCaptureStatus === 'online' || camera.lastCaptureStatus === 'success') {
      return { color: 'bg-emerald-500', label: 'Online', icon: Wifi };
    }
    if (camera.lastCaptureStatus === 'offline' || camera.lastCaptureStatus === 'failed') {
      return { color: 'bg-red-500', label: 'Offline', icon: WifiOff };
    }
    return { color: 'bg-gray-400', label: 'Unknown', icon: Wifi };
  };

  const status = getStatusDisplay();
  const StatusIcon = status.icon;

  // Format schedule display (Phase 11: Now 24/7 operation)
  const scheduleDisplay = '24/7';
  const intervalDisplay = camera.captureIntervalMinutes >= 60
    ? `${camera.captureIntervalMinutes / 60}h`
    : `${camera.captureIntervalMinutes}m`;

  return (
    <div className={`
      group relative rounded-2xl border bg-white overflow-hidden
      transition-all duration-300
      ${camera.isActive
        ? 'border-gray-100 shadow-sm hover:shadow-lg hover:border-gray-200'
        : 'border-gray-100 bg-gray-50 opacity-60'}
    `}>
      {/* Header with Status */}
      <div className="flex items-start justify-between p-5 pb-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`
            relative flex h-11 w-11 items-center justify-center rounded-xl
            ${camera.captureEnabled ? 'bg-guild-mint/50' : 'bg-gray-100'}
          `}>
            <Camera size={22} className={camera.captureEnabled ? 'text-guild-green' : 'text-gray-400'} />
            {/* Status dot */}
            <div className={`absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-white ${status.color}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 truncate">{camera.name}</h3>
            <div className="flex items-center gap-1 group/id">
              <p className="text-xs text-gray-500 truncate font-mono" title={camera.cameraId}>
                {camera.cameraId.length > 15
                  ? `${camera.cameraId.slice(0, 15)}...`
                  : camera.cameraId}
              </p>
              <button
                onClick={handleCopyId}
                className="flex-shrink-0 p-0.5 rounded text-gray-300 hover:text-gray-500 transition-colors opacity-0 group-hover/id:opacity-100"
                title="Copy camera ID"
              >
                {copiedId ? (
                  <Check size={12} className="text-emerald-500" />
                ) : (
                  <Copy size={12} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Actions menu */}
        {isAdmin && (
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <MoreVertical size={16} />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-20 w-48 rounded-xl bg-white border border-gray-100 shadow-xl py-1.5">
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      setActivityPanelOpen(true);
                    }}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
                  >
                    <Zap size={14} />
                    Activity Triggers
                  </button>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit();
                    }}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Pencil size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete();
                    }}
                    disabled={isDeleting}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="px-5 pb-4 space-y-2.5">
        {camera.location && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin size={14} className="text-gray-400 flex-shrink-0" />
            <span className="truncate">{camera.location}</span>
          </div>
        )}
        {camera.department && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Building2 size={14} className="text-gray-400 flex-shrink-0" />
            <span className="truncate">{camera.department.name}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <FileText size={14} className="text-gray-400 flex-shrink-0" />
          <span className="truncate">
            {camera.sops && camera.sops.length > 0
              ? camera.sops.length === 1
                ? camera.sops[0].title
                : `${camera.sops[0].title} +${camera.sops.length - 1} more`
              : <span className="text-gray-400">No SOPs assigned</span>}
          </span>
        </div>
      </div>

      {/* Schedule & Status Bar */}
      <div className="border-t border-gray-100 px-5 py-3 bg-gray-50/50 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Capture Toggle */}
          <div className={`
            flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
            ${camera.captureEnabled
              ? 'bg-guild-green/10 text-guild-green'
              : 'bg-gray-200 text-gray-500'}
          `}>
            {camera.captureEnabled ? <Play size={12} /> : <Pause size={12} />}
            {camera.captureEnabled ? 'Active' : 'Paused'}
          </div>

          {/* Schedule */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Clock size={12} />
            <span>{scheduleDisplay}</span>
            <span className="text-gray-300">|</span>
            <span>Every {intervalDisplay}</span>
          </div>
        </div>
      </div>

      {/* Test Connection Button */}
      <div className="border-t border-gray-100 p-3">
        <button
          onClick={onTest}
          disabled={isTesting}
          className={`
            w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium
            transition-all duration-200
            ${isTesting
              ? 'bg-gray-100 text-gray-400 cursor-wait'
              : testResult?.isOnline
                ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                : testResult && !testResult.isOnline
                  ? 'bg-red-50 text-red-700 hover:bg-red-100'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
          `}
        >
          {isTesting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Testing Connection...
            </>
          ) : testResult?.isOnline ? (
            <>
              <CheckCircle2 size={14} />
              Connected
            </>
          ) : testResult && !testResult.isOnline ? (
            <>
              <XCircle size={14} />
              Connection Failed
            </>
          ) : (
            <>
              <StatusIcon size={14} />
              Test Connection
            </>
          )}
        </button>
        {testResult?.error && (
          <p className="mt-2 text-xs text-red-600 text-center">{testResult.error}</p>
        )}
      </div>

      {/* Activity Trigger Config Panel */}
      <ActivityTriggerConfigPanel
        cameraId={camera.id}
        cameraName={camera.name}
        isOpen={activityPanelOpen}
        onClose={() => setActivityPanelOpen(false)}
      />
    </div>
  );
}
